// AiDisambiguationOptions.jsx — selectable ambiguous-record options

import React, { useState } from 'react';

// CSS
import './AiDisambiguationOptions.css';

/**
 * AiDisambiguationOptions
 * Renders a selectable list of ambiguous-record matches for AI disambiguation.
 * @param {object[]} [options] - Candidate records to choose from
 * @param {boolean} [enabled] - Whether selection is allowed
 * @param {boolean} [lockOnSelect] - When true, locks the list after one pick (resume flows)
 * @param {function} [onSelect] - Called with the chosen option
 * @returns {ReactNode}
 */
const AiDisambiguationOptions = React.memo(({
  options = [],
  enabled = true,
  lockOnSelect = true,
  onSelect = null,
}) => {
  // ----- STATE ----- //

  const [selectedNodeKey, setSelectedNodeKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!Array.isArray(options) || options.length < 2) return null;

  const isLocked = lockOnSelect && Boolean(selectedNodeKey);
  const canSelect = enabled && typeof onSelect === 'function' && !isSubmitting && !isLocked;

  // ----- RENDER ----- //

  return (
    <div
      className="ai-disambiguation"
      role="listbox"
      aria-label="Matching records"
    >
      {options.map((option) => {
        const isSelected = lockOnSelect && selectedNodeKey === option.nodeKey;
        const isDisabled = !canSelect && !isSelected;

        return (
          <button
            className={[
              'ai-disambiguation__option',
              isSelected ? 'is-selected' : '',
            ].filter(Boolean).join(' ')}
            key={option.nodeKey}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={isDisabled || isSubmitting}
            onClick={async () => {
              if (!canSelect) return;
              if (lockOnSelect) {
                setSelectedNodeKey(option.nodeKey);
              }
              setIsSubmitting(true);
              try {
                await onSelect(option);
              } catch (error) {
                console.error('[Ai] disambiguation select failed', error);
                if (lockOnSelect) {
                  setSelectedNodeKey('');
                }
              } finally {
                setIsSubmitting(false);
              }
            }}
          >

            {/* TITLE */}
            <span className="ai-disambiguation__title">
              {option.title}
            </span>

            {/* META */}
            <span className="ai-disambiguation__meta">
              {option.subtitle
                ? `${option.typeLabel} · ${option.subtitle}`
                : option.typeLabel}
            </span>
          </button>
        );
      })}
    </div>
  );
});

// Dev Tools
AiDisambiguationOptions.displayName = 'AiDisambiguationOptions';

export default AiDisambiguationOptions;
